import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import ProposalBuilder from "../components/proposals/ProposalBuilder";
import ProposalView from "../components/proposals/ProposalView";
import InvoiceForm from "../components/proposals/InvoiceForm";
import InvoiceList from "../components/proposals/InvoiceList";
import Button from "../components/common/Button";
import Modal from "../components/common/Modal";
import { proposalService, invoiceService } from "../services/proposals";
import toast from "react-hot-toast";

const Proposals = () => {
  const [activeTab, setActiveTab] = useState("proposals");
  const [isProposalModalOpen, setIsProposalModalOpen] = useState(false);
  const [selectedProposal, setSelectedProposal] = useState(null);
  const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const queryClient = useQueryClient();

  const deleteProposal = useMutation({
    mutationFn: (id) => proposalService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["proposals"] });
      toast.success("Proposal deleted");
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Error deleting proposal");
    },
  });

  const deleteInvoice = useMutation({
    mutationFn: (id) => invoiceService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invoices"] });
      toast.success("Invoice deleted");
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Error deleting invoice");
    },
  });

  const handleEditProposal = (proposal) => {
    setSelectedProposal(proposal);
    setIsProposalModalOpen(true);
  };

  const handleDeleteProposal = (id) => {
    if (window.confirm("Are you sure you want to delete this proposal?")) {
      deleteProposal.mutate(id);
    }
  };

  const handleEditInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setIsInvoiceModalOpen(true);
  };

  const handleDeleteInvoice = (id) => {
    if (window.confirm("Are you sure you want to delete this invoice?")) {
      deleteInvoice.mutate(id);
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Proposals & Invoices</h1>
          <p className="text-sm text-gray-500 mt-1">Manage proposals and invoices</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={activeTab === "proposals" ? "primary" : "secondary"}
            onClick={() => setActiveTab("proposals")}
          >
            Proposals
          </Button>
          <Button
            variant={activeTab === "invoices" ? "primary" : "secondary"}
            onClick={() => setActiveTab("invoices")}
          >
            Invoices
          </Button>
        </div>
        {activeTab === "proposals" ? (
          <Button
            onClick={() => {
              setSelectedProposal(null);
              setIsProposalModalOpen(true);
            }}
          >
            Add Proposal
          </Button>
        ) : (
          <Button
            onClick={() => {
              setSelectedInvoice(null);
              setIsInvoiceModalOpen(true);
            }}
          >
            Add Invoice
          </Button>
        )}
      </div>
      {activeTab === "proposals" ? (
        <ProposalView onEdit={handleEditProposal} onDelete={handleDeleteProposal} />
      ) : (
        <InvoiceList onEdit={handleEditInvoice} onDelete={handleDeleteInvoice} />
      )}
      <Modal
        isOpen={isProposalModalOpen}
        onClose={() => setIsProposalModalOpen(false)}
        title={selectedProposal ? "Edit Proposal" : "Add Proposal"}
      >
        <ProposalBuilder
          proposal={selectedProposal}
          onSuccess={() => setIsProposalModalOpen(false)}
        />
      </Modal>
      <Modal
        isOpen={isInvoiceModalOpen}
        onClose={() => setIsInvoiceModalOpen(false)}
        title={selectedInvoice ? "Edit Invoice" : "Add Invoice"}
      >
        <InvoiceForm
          invoice={selectedInvoice}
          onSuccess={() => setIsInvoiceModalOpen(false)}
        />
      </Modal>
    </div>
  );
};

export default Proposals;
